import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2, Mail, Search, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export const GuildInviteSearch = ({ guildId }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const root = useRef(null);
  const input = useRef(null);
  const [query, setQuery] = useState("");
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [active, setActive] = useState(0);
  const [message, setMessage] = useState("");
  const prefix = `guild-invite-${guildId}`;

  useEffect(() => {
    const timer = setTimeout(() => { setTerm(query.trim()); setPage(1); setActive(0); }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, []);

  const results = useQuery({
    queryKey: ["guild-invite-candidates", guildId, user?.id, term, page],
    queryFn: async ({ signal }) => (await api.get(`/guilds/${guildId}/invite-candidates`, { signal, params: { q: term, page, page_size: 8 } })).data,
    enabled: !!user && !selected && open && term.length >= 2 && term === query.trim(),
    staleTime: 0,
  });
  const waiting = query.trim() !== term || results.isFetching;
  const items = !waiting ? results.data?.items || [] : [];
  const showResults = open && !selected && query.trim().length >= 2;
  const choose = candidate => {
    setSelected(candidate); setQuery(candidate.display_name); setOpen(false); setMessage("");
  };
  const invite = useMutation({
    mutationFn: async ({ id, invitationNote }) => (await api.post(`/guilds/${guildId}/invite`, { user_id: id, note: invitationNote || null })).data,
    onSuccess: data => {
      const text = data.already ? "An invitation is already pending." : "Invitation sent.";
      toast.success(text); setMessage(text); setSelected(null); setQuery(""); setNote(""); setOpen(false);
      qc.invalidateQueries({ queryKey: ["guild-invite-candidates", guildId] });
    },
    onError: error => {
      setMessage(""); toast.error(apiError(error, "Couldn't send the invitation."));
      qc.invalidateQueries({ queryKey: ["guild-invite-candidates", guildId] });
      if (error.response?.status === 409) setSelected(null);
      if (error.response?.status === 403) qc.invalidateQueries({ queryKey: ["guild", guildId] });
    },
  });
  const keyDown = event => {
    if (event.key === "Escape") { setOpen(false); return; }
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key) || selected) return;
    if (event.key !== "Enter") { event.preventDefault(); setOpen(true); }
    if (!items.length) return;
    if (event.key === "ArrowDown") setActive(i => Math.min(i + 1, items.length - 1));
    if (event.key === "ArrowUp") setActive(i => Math.max(0, i - 1));
    if (event.key === "Enter" && showResults) { event.preventDefault(); choose(items[Math.min(active, items.length - 1)]); }
  };

  return <div ref={root} className="min-w-0 space-y-3" data-testid={`${prefix}-search-form`}>
    <label htmlFor={`${prefix}-search`} className="text-xs text-white/70">Player name</label>
    <div className="relative">
      <Search aria-hidden="true" className="absolute left-3 top-3 h-4 w-4 text-white/40" />
      <Input ref={input} id={`${prefix}-search`} data-testid={`${prefix}-search`} role="combobox" autoComplete="off"
        aria-autocomplete="list" aria-expanded={showResults} aria-controls={`${prefix}-results`}
        aria-activedescendant={showResults && items[active] ? `${prefix}-candidate-${items[active].id}` : undefined}
        placeholder="Search players…" maxLength={80} value={query} disabled={invite.isPending}
        onFocus={() => !selected && setOpen(true)} onKeyDown={keyDown}
        onChange={event => { setQuery(event.target.value); setSelected(null); setOpen(true); setMessage(""); invite.reset(); }}
        className="bg-white/5 border-white/15 text-white pl-9 pr-10" />
      {query && <button data-testid={`${prefix}-clear`} aria-label="Clear player selection" disabled={invite.isPending}
        onClick={() => { setQuery(""); setSelected(null); setOpen(false); input.current?.focus(); }}
        className="absolute right-1 top-1 grid h-8 w-8 place-items-center rounded hover:bg-white/10 text-white/60 transition-colors"><X className="h-4 w-4" /></button>}
    </div>
    {showResults && <div className="min-w-0 rounded-lg border border-white/15 bg-black/30 overflow-hidden">
      {waiting && <p data-testid={`${prefix}-loading`} role="status" className="flex items-center gap-2 p-3 text-xs text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</p>}
      {results.isError && !waiting && <div role="alert" data-testid={`${prefix}-search-error`} className="p-3 text-xs text-amber-200">{apiError(results.error, "Search unavailable.")} <button data-testid={`${prefix}-retry`} onClick={() => results.refetch()} className="underline">Retry</button></div>}
      {!waiting && !results.isError && !items.length && <p data-testid={`${prefix}-empty`} role="status" className="p-3 text-xs text-white/50">No eligible players found.</p>}
      <div id={`${prefix}-results`} role="listbox" aria-label="Eligible players" data-testid={`${prefix}-results`} className="max-h-64 overflow-y-auto">
        {items.map((candidate, index) => <button key={candidate.id} id={`${prefix}-candidate-${candidate.id}`}
          data-testid={`${prefix}-candidate-${candidate.id}`} role="option" aria-selected={index === active}
          onMouseEnter={() => setActive(index)} onClick={() => choose(candidate)}
          className={`flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-left transition-colors ${index === active ? "bg-cyan-400/10" : "hover:bg-white/5"}`}>
          <UserRound className="h-4 w-4 shrink-0 text-cyan-200" />
          <span className="min-w-0"><span className="block break-words text-sm text-white">{candidate.display_name}</span>
            <span className="block break-all text-[10px] text-white/45">{candidate.username ? `@${candidate.username}` : `…${candidate.id.slice(-8)}`}</span></span>
        </button>)}
      </div>
      {!waiting && results.data?.total_pages > 1 && <div className="flex items-center justify-between gap-2 border-t border-white/10 px-3 py-2">
        <button data-testid={`${prefix}-previous`} aria-label="Previous players" disabled={page <= 1} onClick={() => { setPage(p => p - 1); setActive(0); }} className="p-1 text-white/70 disabled:opacity-25"><ChevronLeft className="h-4 w-4" /></button>
        <span data-testid={`${prefix}-page`} className="text-xs text-white/50">{results.data.page} / {results.data.total_pages}</span>
        <button data-testid={`${prefix}-next`} aria-label="Next players" disabled={page >= results.data.total_pages} onClick={() => { setPage(p => p + 1); setActive(0); }} className="p-1 text-white/70 disabled:opacity-25"><ChevronRight className="h-4 w-4" /></button>
      </div>}
    </div>}
    {selected && <p data-testid={`${prefix}-selected`} className="text-xs text-cyan-200 break-words">Invite {selected.display_name}</p>}
    <Textarea value={note} onChange={event => setNote(event.target.value)} aria-label="Invitation message" placeholder="Optional message" maxLength={280} rows={2}
      disabled={invite.isPending} data-testid={`${prefix}-note`} className="bg-white/5 border-white/10 text-white text-xs" />
    <Button size="sm" onClick={() => selected && invite.mutate({ id: selected.id, invitationNote: note.trim() })} disabled={invite.isPending || !selected}
      data-testid={`${prefix}-send`} className="bg-cyan-400 text-black hover:bg-cyan-300"><Mail className="h-3 w-3 mr-2" /> {invite.isPending ? "Sending…" : "Send invite"}</Button>
    {message && <p data-testid={`${prefix}-confirmation`} role="status" className="text-xs text-emerald-200">{message}</p>}
    {invite.isError && <p data-testid={`${prefix}-invite-error`} role="alert" className="text-xs text-amber-200">{apiError(invite.error, "Invitation failed.")}</p>}
  </div>;
};